import { useEffect, useState } from "react";
import { Mail, Plus, Send, StickyNote, Building2, ArrowLeft, ArrowDownLeft, ArrowUpRight, Paperclip, X, FileText } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { downloadFromUrl } from "@/lib/download";
import { Card } from "@/components/ui/card";
import { CollapsibleCard, useSeen } from "@/components/CollapsibleCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_TONE = {
  open: "bg-green-100 text-green-700 border-green-200",
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  closed: "bg-slate-100 text-slate-500 border-slate-200",
};
const fmt = (s) => (s ? new Date(s).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
const MAX_FILE = 10 * 1024 * 1024;

const readFiles = (fileList) => Promise.all(Array.from(fileList).map((f) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res({ filename: f.name, content_type: f.type || "application/octet-stream", data: r.result, size: f.size });
  r.onerror = rej;
  r.readAsDataURL(f);
})));
const stripForApi = (files) => files.map((f) => ({ filename: f.filename, content_type: f.content_type, data: f.data }));

const AttachPicker = ({ files, setFiles, testid }) => {
  const onPick = async (e) => {
    const picked = await readFiles(e.target.files);
    const tooBig = picked.find((p) => p.size > MAX_FILE);
    if (tooBig) { toast.error(`${tooBig.filename} exceeds the 10 MB limit`); e.target.value = ""; return; }
    setFiles((arr) => [...arr, ...picked]);
    e.target.value = "";
  };
  return (
    <div className="space-y-1.5">
      <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-primary hover:underline" data-testid={`${testid}-label`}>
        <Paperclip className="h-3.5 w-3.5" /> Attach files
        <input type="file" multiple className="hidden" onChange={onPick} data-testid={testid} />
      </label>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {files.map((f, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-xs text-slate-600" data-testid={`${testid}-file-${i}`}>
              <FileText className="h-3 w-3" /> <span className="max-w-[160px] truncate">{f.filename}</span>
              <button type="button" onClick={() => setFiles((arr) => arr.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const AttachmentChips = ({ attachments }) => {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {attachments.map((a) => (
        <button key={a.file_id} onClick={() => downloadFromUrl(`/ticket-files/${a.file_id}`, a.filename).catch(() => toast.error("Download failed"))} data-testid={`attach-${a.file_id}`}
          className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 hover:border-primary hover:text-primary">
          <Paperclip className="h-3 w-3" /> <span className="max-w-[180px] truncate">{a.filename}</span> <span className="text-slate-400">{fmtSize(a.size)}</span>
        </button>
      ))}
    </div>
  );
};

const NewTicketDialog = ({ projectId, onClose, onCreated }) => {
  const [form, setForm] = useState({ subject: "", vendor_name: "", vendor_email: "", body: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.subject.trim() || !form.vendor_email.trim() || !form.body.trim()) { toast.error("Subject, vendor email and message are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/tickets`, { ...form, attachments: stripForApi(files) });
      toast.success(data.email_sent ? "Ticket opened & email sent" : "Ticket opened (email not sent — check config)");
      onCreated(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to open ticket"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-primary" /> New vendor ticket</DialogTitle>
          <DialogDescription>Emails the vendor from project@iplogin.io. Their replies thread back here automatically. Internal staff only.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Vendor name</Label><Input data-testid="ticket-vendor-name" value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} placeholder="Bell Canada" /></div>
            <div className="space-y-1.5"><Label>Vendor email *</Label><Input data-testid="ticket-vendor-email" type="email" value={form.vendor_email} onChange={(e) => setForm({ ...form, vendor_email: e.target.value })} placeholder="rep@vendor.com" /></div>
          </div>
          <div className="space-y-1.5"><Label>Subject *</Label><Input data-testid="ticket-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Porting LOA request" /></div>
          <div className="space-y-1.5"><Label>Message *</Label><Textarea data-testid="ticket-body" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Write your email to the vendor…" /></div>
          <AttachPicker files={files} setFiles={setFiles} testid="ticket-attach" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="ticket-send" onClick={submit} disabled={busy} className="bg-primary hover:bg-primary/90 text-primary-foreground"><Send className="h-4 w-4" /> {busy ? "Sending…" : "Send email"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MessageRow = ({ m }) => {
  if (m.direction === "note") {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid={`ticket-msg-${m.message_id}`}>
        <div className="flex items-center gap-2 text-xs text-amber-700"><StickyNote className="h-3.5 w-3.5" /><span className="font-medium">{m.by_name}</span><Badge variant="outline" className="border-amber-300 bg-amber-100 text-[10px] text-amber-700">Internal note</Badge><span className="text-amber-600/70">· {fmt(m.at)}</span></div>
        <p className="mt-1 whitespace-pre-wrap text-sm text-amber-900">{m.body}</p>
        <AttachmentChips attachments={m.attachments} />
      </div>
    );
  }
  const inbound = m.direction === "inbound";
  return (
    <div className={`rounded-lg border px-3 py-2 ${inbound ? "border-blue-200 bg-blue-50" : "border-slate-200 bg-white"}`} data-testid={`ticket-msg-${m.message_id}`}>
      <div className="flex items-center gap-2 text-xs text-slate-500">
        {inbound ? <ArrowDownLeft className="h-3.5 w-3.5 text-blue-500" /> : <ArrowUpRight className="h-3.5 w-3.5 text-slate-400" />}
        <span className="font-medium text-slate-700">{inbound ? (m.from_name || m.from_email) : m.by_name}</span>
        <span className="text-slate-400">{inbound ? "→ us" : `→ ${m.to_email}`}</span>
        <span className="text-slate-400">· {fmt(m.at)}</span>
      </div>
      <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{m.body}</p>
      <AttachmentChips attachments={m.attachments} />
    </div>
  );
};

const TicketThread = ({ ticket, onBack, onUpdated }) => {
  const [reply, setReply] = useState("");
  const [mode, setMode] = useState("email"); // email | note
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const url = mode === "note" ? `/tickets/${ticket.ticket_id}/note` : `/tickets/${ticket.ticket_id}/reply`;
      const body = mode === "note" ? { body: reply } : { body: reply, attachments: stripForApi(files) };
      const { data } = await api.post(url, body);
      setReply(""); setFiles([]);
      if (mode === "email" && data.email_sent === false) toast.error("Saved, but email failed to send");
      else toast.success(mode === "note" ? "Note added" : "Reply sent");
      onUpdated(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    try { const { data } = await api.put(`/tickets/${ticket.ticket_id}`, { status }); onUpdated(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="ticket-thread">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="ticket-back"><ArrowLeft className="h-4 w-4" /> All tickets</Button>
        <Select value={ticket.status} onValueChange={setStatus}>
          <SelectTrigger className="h-8 w-[130px] capitalize" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>{["open", "pending", "closed"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <div className="flex items-center gap-2"><span className="text-sm font-semibold text-slate-800">{ticket.subject}</span><Badge variant="outline" className="font-mono text-[10px] text-slate-500">#T-{ticket.seq}</Badge></div>
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500"><Building2 className="h-3.5 w-3.5" />{ticket.vendor_name || ticket.vendor_email} <span className="text-slate-400">· {ticket.vendor_email}</span></div>
      </div>
      <div className="max-h-80 space-y-2 overflow-y-auto rounded-lg bg-slate-50 p-3" data-testid="ticket-messages">
        {(ticket.messages || []).map((m) => <MessageRow key={m.message_id} m={m} />)}
      </div>
      <div className="rounded-lg border border-slate-200 p-3">
        <div className="mb-2 flex items-center gap-2">
          <Button size="sm" variant={mode === "email" ? "default" : "outline"} className={mode === "email" ? "bg-primary text-primary-foreground" : ""} onClick={() => setMode("email")} data-testid="ticket-mode-email"><Send className="h-3.5 w-3.5" /> Email vendor</Button>
          <Button size="sm" variant={mode === "note" ? "default" : "outline"} className={mode === "note" ? "bg-amber-500 text-white hover:bg-amber-500/90" : ""} onClick={() => setMode("note")} data-testid="ticket-mode-note"><StickyNote className="h-3.5 w-3.5" /> Internal note</Button>
        </div>
        <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} data-testid="ticket-reply-input" placeholder={mode === "note" ? "Add an internal note (not emailed)…" : `Reply to ${ticket.vendor_email}…`} />
        <div className="mt-2 flex items-center justify-between gap-2">
          {mode === "email" ? <AttachPicker files={files} setFiles={setFiles} testid="ticket-reply-attach" /> : <span />}
          <Button size="sm" onClick={send} disabled={busy} data-testid="ticket-reply-send" className="bg-primary hover:bg-primary/90 text-primary-foreground">{busy ? "Sending…" : mode === "note" ? "Add note" : "Send email"}</Button>
        </div>
      </div>
    </div>
  );
};

export const VendorTickets = ({ projectId }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const load = () => { setLoading(true); api.get(`/projects/${projectId}/tickets`).then((r) => setTickets(r.data || [])).catch(() => setTickets([])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  const selected = tickets.find((t) => t.ticket_id === openId);
  const applyUpdate = (doc) => { setTickets((arr) => arr.map((t) => (t.ticket_id === doc.ticket_id ? doc : t))); };
  const latestInbound = tickets.reduce((m, t) => { for (const x of (t.messages || [])) { if (x.direction === "inbound" && x.at > m) m = x.at; } return m; }, "");
  const veSeen = useSeen(`proj-${projectId}-vendor-emails`, latestInbound);

  return (
    <CollapsibleCard
      testid="vendor-tickets"
      icon={Mail}
      title="Vendor Emails"
      persistKey={`proj-${projectId}-vendor-emails`}
      defaultOpen={false}
      count={tickets.length}
      dot={veSeen.unread ? "red" : (tickets.length ? "green" : "slate")}
      dotTitle={veSeen.unread ? "New vendor reply" : "Up to date"}
      onOpenChange={(open) => { if (open) veSeen.markSeen(); }}
      badge={<Badge variant="outline" className="text-[10px] text-slate-500">staff only</Badge>}
      headerRight={!selected ? <Button size="sm" variant="outline" onClick={() => setNewOpen(true)} data-testid="new-ticket-button"><Plus className="h-4 w-4" /> New ticket</Button> : null}
    >
      <div className="p-5">
        {selected ? (
          <TicketThread ticket={selected} onBack={() => setOpenId(null)} onUpdated={applyUpdate} />
        ) : loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : tickets.length === 0 ? (
          <p className="text-sm text-slate-400">No vendor tickets yet. Open one to email a 3rd party about this project.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {tickets.map((t) => {
              const last = (t.messages || [])[t.messages.length - 1];
              return (
                <button key={t.ticket_id} onClick={() => setOpenId(t.ticket_id)} data-testid={`ticket-row-${t.ticket_id}`} className="flex w-full items-center justify-between gap-3 py-2.5 text-left hover:bg-slate-50">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2"><span className="truncate text-sm font-medium text-slate-800">{t.subject}</span><Badge variant="outline" className="shrink-0 font-mono text-[10px] text-slate-400">#T-{t.seq}</Badge></div>
                    <div className="truncate text-xs text-slate-400">{t.vendor_name || t.vendor_email}{last ? ` · ${last.direction === "inbound" ? "↩ " : ""}${(last.body || "").slice(0, 60)}` : ""}</div>
                  </div>
                  <Badge variant="outline" className={`shrink-0 capitalize ${STATUS_TONE[t.status] || ""}`}>{t.status}</Badge>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {newOpen && <NewTicketDialog projectId={projectId} onClose={() => setNewOpen(false)} onCreated={(d) => { setNewOpen(false); setTickets((arr) => [d, ...arr]); setOpenId(d.ticket_id); }} />}
    </CollapsibleCard>
  );
};
